import { MutableRefObject, useEffect, useRef, useState } from "react";
import { Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { Map } from "ol";
import { IMapProps } from "../Providers/mapProvider";
import { useAppContext } from "../Contexts/AppContext";
import { useMapContext } from "../Contexts/MapContext";
import { useMapDownloadContext } from "../Contexts/MapDownloadContext";
import { addWMTSLayersAsync } from "./wmtsLayers";
import { switchBasemap } from "./basemapFunctions";
import { switchLayers } from "./layerFunctions";
import {
    changeHighlightFeatures,
    changeHighlightSheetFeatures,
    createMap,
    onClick,
    onDoubleClick,
    onHoverCluster,
    onMoveEnd,
    onPointerMove,
    resetClickedCluster,
    zoomToFeature
} from "./mapFunctions";
import { DrawType, SheetSelectMode } from "./enums";
import { createClusters, createClusterSource } from "./clusterFunctions";

export const useMap = (target: MutableRefObject<HTMLDivElement>, props: IMapProps): Map => {
    const { hasMap, locateFeature, highlightFeatures, setMapClick } = useAppContext();
    const {
        configA,
        layerStates,
        baseMap,
        drawType,
        hoveredCluster,
        featuresPopup,
        setHoveredCluster,
        setHoveredFeature,
        setFeaturesPopup
    } = useMapContext();
    const { sheetSelectMode, sheetFeatures } = useMapDownloadContext();

    const [animating, setAnimating] = useState<boolean>();
    const [animationId, setAnimationId] = useState<number>();

    const _map = useRef<Map>();
    const highLightSourceRef = useRef<VectorSource>(new VectorSource());
    const highLightClusterSourceRef = useRef<VectorSource>(new VectorSource());
    const highLightSheetSourceRef = useRef<VectorSource>(new VectorSource());
    const hoveredSourceRef = useRef<VectorSource>(new VectorSource());
    const clickedClusterSourceRef = useRef<VectorSource>(new VectorSource());
    const clusterSourceRef = useRef<ClusterSource>(createClusterSource());
    const sheetSelectModeRef = useRef<SheetSelectMode>();

    const drawTypeRef = useRef<DrawType>(drawType);

    useEffect(() => {
        if (!target.current || !hasMap || !configA) {
            return;
        }

        _map.current = createMap(
            configA,
            target.current,
            highLightSourceRef.current,
            highLightClusterSourceRef.current,
            highLightSheetSourceRef.current,
            hoveredSourceRef.current,
            clickedClusterSourceRef.current
        );

        // This is to add WMTS layers async, using GetCapabilities request
        // const wmtsLayers: IBasemap[] = configA.basemaps.filter((b) => b.type === "WMTS");
        // wmtsLayers?.length && addWMTSLayersAsync(wmtsLayers, baseMap);

        configA.layers.find((l) => l.clusterSharingLayer) &&
            createClusters(_map.current, configA, clusterSourceRef.current);

        _map.current.on("moveend", () => {
            onMoveEnd();
            setHoveredCluster(null);
            setAnimationId(null);
        });
        _map.current.on("pointermove", (e) =>
            onPointerMove(e, target.current, configA, setHoveredCluster, setHoveredFeature)
        );
        _map.current.on("singleclick", (e) =>
            onClick(
                e,
                setMapClick,
                setFeaturesPopup,
                drawTypeRef.current,
                highLightClusterSourceRef.current,
                clickedClusterSourceRef.current
            )
        );
        _map.current.on("dblclick", (e) =>
            onDoubleClick(e, setMapClick, drawTypeRef.current, sheetSelectModeRef.current)
        );

        _map.current.once("rendercomplete", () => document.dispatchEvent(new Event("rendercomplete")));
        //@ts-ignore
        window.map = _map.current;
    }, [hasMap, configA]);

    useEffect(() => layerStates && switchLayers(layerStates), [layerStates]);
    useEffect(() => baseMap && switchBasemap(baseMap), [baseMap]);
    useEffect(() => changeHighlightFeatures(highlightFeatures, highLightSourceRef.current), [highlightFeatures]);
    useEffect(() => changeHighlightSheetFeatures(sheetFeatures, highLightSheetSourceRef.current), [sheetFeatures]);
    useEffect(() => locateFeature && zoomToFeature(locateFeature.feature), [locateFeature]);
    useEffect(() => !featuresPopup && resetClickedCluster(clickedClusterSourceRef.current), [featuresPopup]);
    useEffect(() => {
        sheetSelectModeRef.current = sheetSelectMode;
    }, [sheetSelectMode]);
    useEffect(() => {
        onHoverCluster(
            hoveredCluster,
            animationId,
            setAnimationId,
            animating,
            setAnimating,
            highLightClusterSourceRef.current
        );
    }, [hoveredCluster, animating, animationId]);

    //@ts-ignore
    window.map = _map.current;
    return _map.current;
};
